/**
 * Storage Service
 * 
 * Provides a clean abstraction layer over localStorage with:
 * - Type safety
 * - Error handling
 * - Validation
 * - Consistent key prefixing
 */

const STORAGE_PREFIX = 'portfolio-';

export type StorageKey = 
  | 'theme'
  | 'mode'
  | 'color-scheme'
  | 'custom-color'
  | 'title-color-override'
  | 'body-color-override';

/**
 * Get an item from localStorage with optional type casting
 */
export const getStorageItem = <T = string>(key: StorageKey): T | null => {
  try {
    const value = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (value === null) return null;
    
    // Try to parse JSON, fallback to string
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as T;
    }
  } catch (error) {
    console.error(`[Storage] Error reading ${key}:`, error);
    return null;
  }
};

/**
 * Set an item in localStorage with automatic JSON stringification
 */
export const setStorageItem = <T = string>(key: StorageKey, value: T): boolean => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, stringValue);
    return true;
  } catch (error) {
    console.error(`[Storage] Error writing ${key}:`, error);
    return false;
  }
};

/**
 * Remove an item from localStorage
 */
export const removeStorageItem = (key: StorageKey): boolean => {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    return true;
  } catch (error) {
    console.error(`[Storage] Error removing ${key}:`, error);
    return false;
  }
};

/**
 * Clear all portfolio-related items from localStorage
 */
export const clearStorage = (): boolean => {
  try {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(STORAGE_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('[Storage] Error clearing storage:', error);
    return false;
  }
};

/**
 * Check if localStorage is available
 */
export const isStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get multiple storage items at once
 */
export const getStorageItems = (
  keys: StorageKey[]
): Record<string, any> => {
  const result: Record<string, any> = {};
  keys.forEach(key => {
    const value = getStorageItem(key);
    if (value !== null) {
      result[key] = value;
    }
  });
  return result;
};

